import { FollowerTraitSchema, type FollowerTrait } from '../../schemas/follower';

/**
 * Catalogo delle **capacità utili** note dei gregari (manuale V1, rr. 1322-1338).
 *
 * Il manuale indica esplicitamente che la lista è **aperta**: il GM può
 * inventare altre capacità utili specifiche del singolo gregario. Questa
 * lista contiene gli 11 esempi canonici del manuale, con effetti meccanici
 * strutturati dove applicabile (decisione SSOT D5).
 *
 * **Decisione SSOT D6**: la capacità "devoto a una divinità" (manuale r. 1338,
 * «può usare la mossa *Scacciare Non Morti* come un Chierico») è modellata
 * con `effects.grantsMoveId` puntando alla mossa di classe corrispondente.
 * Pattern strutturato, non solo description.
 */
export const KNOWN_FOLLOWER_TRAITS: Record<string, FollowerTrait> = {
  'combattente-esperto': FollowerTraitSchema.parse({
    id: 'combattente-esperto',
    label: 'Combattente esperto',
    description: 'Il dado di Danno è 1d8 invece che 1d6.',
    effects: { damageDieOverride: 'd8' },
  }),
  'vede-al-buio': FollowerTraitSchema.parse({
    id: 'vede-al-buio',
    label: 'Vede al buio',
    description: 'Può vedere al buio.',
  }),
  robusto: FollowerTraitSchema.parse({
    id: 'robusto',
    label: 'Robusto',
    description: '+3 PF.',
    effects: { hpDelta: 3 },
  }),
  alpinista: FollowerTraitSchema.parse({
    id: 'alpinista',
    label: 'Alpinista',
    description: 'Non deve *sfidare il pericolo* per arrampicarsi.',
  }),
  'pelle-squamosa': FollowerTraitSchema.parse({
    id: 'pelle-squamosa',
    label: 'Pelle squamosa',
    description: '+1 Armatura.',
    effects: { armorDelta: 1 },
  }),
  'ha-le-ali': FollowerTraitSchema.parse({
    id: 'ha-le-ali',
    label: 'Ha le ali',
    description: 'Può volare.',
  }),
  instancabile: FollowerTraitSchema.parse({
    id: 'instancabile',
    label: 'Instancabile',
    description: 'Ha Carico 9 invece che 6.',
    effects: { loadDelta: 3 },
  }),
  'ottima-reputazione': FollowerTraitSchema.parse({
    id: 'ottima-reputazione',
    label: 'Ottima reputazione',
    description: 'Le interazioni sociali sono facilitate.',
  }),
  'pessima-reputazione': FollowerTraitSchema.parse({
    id: 'pessima-reputazione',
    label: 'Pessima reputazione',
    description: 'Le interazioni sociali sono complicate.',
  }),
  erborista: FollowerTraitSchema.parse({
    id: 'erborista',
    label: 'Erborista',
    description:
      'Trasforma un risultato di 6- in un 7-9 quando *declama conoscenze* riguardo la botanica.',
  }),
  'devoto-divinita': FollowerTraitSchema.parse({
    id: 'devoto-divinita',
    label: 'Devoto a una divinità',
    description: 'Può usare la mossa *Scacciare Non Morti* come un Chierico.',
    effects: { grantsMoveId: 'chierico.scacciare-non-morti' },
  }),
  'parla-con-animali': FollowerTraitSchema.parse({
    id: 'parla-con-animali',
    label: 'Parla con gli animali',
    description: 'Sa parlare con gli animali.',
  }),
};

/** Lista ordinata delle capacità note (utile per UI di selezione). */
export const KNOWN_FOLLOWER_TRAIT_LIST: readonly FollowerTrait[] =
  Object.values(KNOWN_FOLLOWER_TRAITS);
